
/**
 * 异步方法节流
 * 1. 在缓存有效期内缓存结果
 * 2. 当同时发起多个同样的异步方法时，只执行一次，且返回同一个结果
 * 3. 在缓存有效期外，重新执行异步方法
 */
export type DecoratorAsyncCache = <T extends (...args: any[]) => any>(
  fn: T,
  cacheTime?: number
) => (...args: Parameters<T>) => Promise<Awaited<ReturnType<T>>>
export const decoratorAsyncCache = <T extends (...args: any[]) => any>(fn: T, cacheTime: number = 0) => {
  let isCalled = false
  let cacheRes: Awaited<ReturnType<T>> | null = null
  let cacheExpireTime = Date.now() + cacheTime 
  let callbackFnList: Array<{
    resolve: (value: Awaited<ReturnType<T>>) => void
    reject: (reason?: any) => void
  }> = []

  const trigger = async (...args: any[]): Promise<Awaited<ReturnType<T>>> => {
    // 如果在缓存有效期内，直接返回缓存结果
    if (cacheExpireTime > Date.now() && cacheRes !== null) {
      return cacheRes
    }

    // 如果正在执行，则将当前调用加入等待队列
    if (isCalled) {
      return new Promise<Awaited<ReturnType<T>>>((resolve, reject) => {
        callbackFnList.push({ resolve, reject })
      })
    }

    isCalled = true

    try {
      cacheRes = await fn(...args) // 执行异步方法
      cacheExpireTime = Date.now() + cacheTime // 更新缓存过期时间

      // 将结果返回给所有等待的调用者
      callbackFnList.forEach(({ resolve }) => {
        resolve(cacheRes as Awaited<ReturnType<T>>)
      })
      callbackFnList = []
      
      return cacheRes as Awaited<ReturnType<T>>
    } catch (error) {
      // 如果执行失败，将错误传递给所有等待的调用者
      callbackFnList.forEach(({ reject }) => {
        reject(error)
      })
      callbackFnList = []
      
      throw error
    } finally {
      isCalled = false
    }
  }

  return trigger
}